import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { getSystemConfig, getApiEndpoints, updateRateLimiting } from '../../../api/systemApi';
import RateLimitingSettings from './RateLimitingSettings';
import EndpointDirectory from './EndpointDirectory';

const ApiManagementTab = () => {
    const [rateLimitingSettings, setRateLimitingSettings] = useState({
        rate_limiting_enabled: false,
        requests_per_minute: 100,
        burst_limit: 200
    });
    const [endpoints, setEndpoints] = useState([]);
    const [rateLimitingInfo, setRateLimitingInfo] = useState({
        enabled: false,
        selective: true,
        rateLimitedRoutes: 0,
        unlimitedRoutes: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRouteDetails, setShowRouteDetails] = useState(false);

    // Fetch initial data
    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const [configData, endpointsData] = await Promise.all([
                getSystemConfig(),
                getApiEndpoints()
            ]);
            
            // Extract rate limiting settings from config
            setRateLimitingSettings({
                rate_limiting_enabled: configData.rate_limiting_enabled || false,
                requests_per_minute: configData.requests_per_minute || 100,
                burst_limit: configData.burst_limit || 200
            });
            
            setEndpoints(endpointsData.endpoints || []);
            
            // Extract rate limiting info
            setRateLimitingInfo({
                enabled: configData.rate_limiting_enabled || false,
                selective: true,
                rateLimitedRoutes: endpointsData.statistics.rateLimiting?.rateLimited || 0,
                unlimitedRoutes: endpointsData.statistics.rateLimiting?.unlimited || 0
            });
        } catch (err) {
            console.error('Failed to fetch API management data:', err);
            setError('Failed to load API management data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Listen for global refresh events
    useEffect(() => {
        const handleGlobalRefresh = () => {
            fetchData();
        };

        window.addEventListener('globalRefresh', handleGlobalRefresh);
        return () => window.removeEventListener('globalRefresh', handleGlobalRefresh);
    }, []);

    const handleRateLimitingChange = async (newSettings) => {
        try {
            console.log('🔄 ApiManagementTab: Updating rate limiting settings:', newSettings);
            setError(null);
            const result = await updateRateLimiting(newSettings);
            console.log('✅ ApiManagementTab: Rate limiting API response:', result);
            setRateLimitingSettings(newSettings);
            console.log('✅ ApiManagementTab: Rate limiting settings updated successfully');
        } catch (err) {
            console.error('❌ ApiManagementTab: Failed to update rate limiting settings:', err);
            setError('Failed to update rate limiting settings');
            throw err;
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-row justify-between items-center">
                    <h3 className="subheader-textbox">
                        {/* <Icon icon='material-symbols:api-rounded' className="text-xl text-primary" /> */}
                        API Management
                    </h3>
                </div>
                <div className="flex items-center justify-center p-8">
                    <div className="text-font-secondary">Loading API management data...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-row justify-between items-center">
                <h3 className="subheader-textbox">
                    {/* <Icon icon='material-symbols:api-rounded' className="text-xl text-primary" /> */}
                    API Management
                </h3>
            </div>

            {/* Error Display */}
            {error && (
                <div className="p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:alert-circle" className="text-red-500" />
                        <span className="text-red-300 text-sm font-medium">Error</span>
                    </div>
                    <p className="text-red-200 text-xs mt-1">{error}</p>
                </div>
            )}

            {/* Rate Limiting Status Dashboard */}
            <div className="card-container p-3 rounded-md gap-2 flex flex-col items-start md:flex-row md:items-center justify-between">
                <div className="flex items-center w-fit flex-row gap-3">
                    <h4 className="font-semibold text-font-primary flex items-center gap-1 text-base">
                        <Icon icon="mdi:speedometer" className="text-2xl text-primary" />
                        Rate Limiting Status
                    </h4>
                    <button
                        onClick={() => setShowRouteDetails(!showRouteDetails)}
                        className="flex items-center gap-0.5 text-sm font-semibold text-primary hover:text-secondary rounded-md transition-colors"
                    >
                        Details
                        <Icon 
                            icon={showRouteDetails ? "mdi:chevron-up" : "mdi:chevron-down"} 
                            className="text-xl" 
                        />
                    </button>
                </div>
                
                <div className="flex flex-row w-fit gap-8 items-center">
                    <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${rateLimitingInfo.enabled ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                        <div>
                            <div className="text-sm font-semibold">
                                {rateLimitingInfo.enabled ? 'ENABLED' : 'DISABLED'}
                            </div>
                            <div className="text-xs text-font-secondary">
                                {rateLimitingInfo.selective ? 'Selective Rate Limiting' : 'Global Rate Limiting'}
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <Icon icon="mdi:clock-fast" className="text-primary text-2xl" />
                        <div>
                            <div className="text-sm font-semibold">
                                {rateLimitingSettings.requests_per_minute} req/min
                            </div>
                            <div className="text-xs text-font-secondary">Rate Limit</div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <Icon icon="mdi:shield-crown" className="text-yellow-500 text-2xl" />
                        <div>
                            <div className="text-sm font-semibold">Admin Bypass</div>
                            <div className="text-xs text-font-secondary">Enabled</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Route Categories - Conditionally Rendered */}
            {showRouteDetails && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="card-container p-4 rounded flex flex-col gap-2">
                    <h4 className="font-semibold flex items-center gap-1">
                        <Icon icon="mdi:alert-circle" className="text-xl text-red-500" />
                        Rate Limited Routes
                    </h4>
                    <div className="space-y-2 text-base px-2">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/auth0/*</span>
                            <span className="text-font-secondary text-sm">User Management</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/customer-bootstrap/*</span>
                            <span className="text-font-secondary text-sm">Setup</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/v1/system/*</span>
                            <span className="text-font-secondary text-sm">Configuration</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/zoho/desk/*</span>
                            <span className="text-font-secondary text-sm">Tickets</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/customers/*</span>
                            <span className="text-font-secondary text-sm">Data Access</span>
                        </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-bgAccent">
                        <span className="text-sm text-font-secondary">
                            {rateLimitingInfo.rateLimitedRoutes} route groups rate limited
                        </span>
                    </div>
                </div>

                <div className="card-container p-4 rounded flex flex-col gap-2">
                    <h4 className="font-semibold flex items-center gap-1">
                        <Icon icon="mdi:check-circle" className="text-xl text-green-500" />
                        Unlimited Routes
                    </h4>
                    <div className="space-y-2 text-base px-2">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/transcripts/*</span>
                            <span className="text-font-secondary text-sm">Read-only</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/notes/*</span>
                            <span className="text-font-secondary text-sm">Read-only</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/activities/*</span>
                            <span className="text-font-secondary text-sm">Read-only</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/system-health/*</span>
                            <span className="text-font-secondary text-sm">Health</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-mono text-base">/api/feedback/*</span>
                            <span className="text-font-secondary text-sm">Feedback</span>
                        </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-bgAccent">
                        <span className="text-sm text-font-secondary">
                            {rateLimitingInfo.unlimitedRoutes} route groups unlimited
                        </span>
                    </div>
                </div>
            </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <RateLimitingSettings 
                    settings={rateLimitingSettings}
                    onSettingsChange={handleRateLimitingChange}
                />
                <EndpointDirectory 
                    endpoints={endpoints}
                />
            </div>
        </div>
    );
};

export default ApiManagementTab;
